/**
 * `/mentor` — the learner's AI-mentor hub (SCH-AI-4, SCHOOLOGY_STUDENT_AI_PLAN
 * Slice 4).
 *
 * Two sections over two queries:
 *   - "Conversations" from `learning.learner.tutor.list` — the caller's own
 *     sessions (course title, first-message snippet, last activity, message
 *     count); clicking one opens `<MentorDock>` for that session's course
 *     (tutor.start reuses the most recent session per course, so the same
 *     thread resumes).
 *   - "Start a new conversation" from `learning.courses.catalog` (the same
 *     query `/catalog` uses) filtered to `enrolledAlready` — one start button
 *     per enrolled course.
 *
 * Empty states encourage starting from a course page ("Ask your mentor"
 * floats there) or enrolling first. The dock itself owns all chat behavior.
 */
import * as React from 'react';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import type { inferRouterOutputs } from '@trpc/server';
import { ChevronRight, GraduationCap, MessageCircle, Sparkles } from 'lucide-react';
import { Button, Card, CardContent, EmptyState, Skeleton } from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { RequireAuth } from '../lib/auth.js';
import { useResolvedFeatures } from '../lib/features.js';
import { LearnShell } from '../components/shell.js';
import { MentorDock } from '../components/mentor/mentor-dock.js';

export const Route = createFileRoute('/mentor')({
  component: MentorPage,
});

type RouterOutputs = inferRouterOutputs<AppRouter>;
type TutorSession = RouterOutputs['learning']['learner']['tutor']['list'][number];
type CatalogCourse = RouterOutputs['learning']['courses']['catalog']['courses'][number];

/** Today → local time ("3:42 PM"); otherwise a short local date ("Jun 9"). */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  return d.toDateString() === now.toDateString()
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function MentorPage(): React.JSX.Element {
  return (
    <RequireAuth>
      <LearnShell title="Mentor" wide>
        <MentorContent />
      </LearnShell>
    </RequireAuth>
  );
}

function MentorContent(): React.JSX.Element {
  // SCH-I3-C2: with the AI mentor off (coaching centres) this page redirects
  // instead of rendering an error shell — and the gated query never fires.
  const features = useResolvedFeatures();
  const mentorEnabled = features.data?.aiTutorEnabled === true;
  const sessionsQ = trpc.learning.learner.tutor.list.useQuery(undefined, {
    enabled: mentorEnabled,
  });
  const catalogQ = trpc.learning.courses.catalog.useQuery();
  const [dockCourseId, setDockCourseId] = React.useState<string | null>(null);

  const enrolled = (catalogQ.data?.courses ?? []).filter((c) => c.enrolledAlready);

  if (features.data && !mentorEnabled) {
    return <Navigate to="/catalog" replace />;
  }

  return (
    <div className="space-y-6">
      {/* Hero — chat-forward mentor identity */}
      <div className="animate-fade-in flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-primary/20 bg-gradient-to-br from-grad-green to-grad-blue p-5">
        <div className="flex items-center gap-4">
          <span className="relative flex size-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-brand-blue shadow-sm">
            <Sparkles className="size-6 text-primary-foreground" aria-hidden="true" />
            <span
              className="absolute -bottom-0.5 -right-0.5 size-3.5 rounded-full border-2 border-card bg-primary"
              aria-hidden="true"
            />
          </span>
          <div className="space-y-1">
            <h2 className="text-xl font-semibold tracking-tight text-brand-deep">Your mentor</h2>
            <p className="flex items-center gap-2 text-sm text-brand-deep/70">
              <span className="size-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
              24/7 help inside your courses — guidance that makes it stick, not just answers.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_320px]">
        {/* ===== Conversations (chat-forward main column) ===== */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <MessageCircle className="size-5 text-primary" aria-hidden="true" />
            <h3 className="text-lg font-semibold tracking-tight">Conversations</h3>
          </div>
          {sessionsQ.isPending ? (
            <div className="grid grid-cols-1 gap-3">
              {Array.from({ length: 3 }, (_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-xl" />
              ))}
            </div>
          ) : sessionsQ.error ? (
            <EmptyState
              icon={MessageCircle}
              title="Couldn’t load your conversations"
              description={sessionsQ.error.message}
              action={
                <Button variant="outline" size="sm" onClick={() => void sessionsQ.refetch()}>
                  Try again
                </Button>
              }
            />
          ) : sessionsQ.data.length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title="No conversations yet"
              description="Open a course and tap “Ask your mentor” — or start one from any course you’re enrolled in."
            />
          ) : (
            <div className="ls-stagger grid grid-cols-1 gap-3">
              {sessionsQ.data.map((session) => (
                <SessionRow
                  key={session.sessionId}
                  session={session}
                  onOpen={() => setDockCourseId(session.courseId)}
                />
              ))}
            </div>
          )}
        </section>

        {/* ===== Start a new conversation (rail) ===== */}
        <aside className="space-y-3 lg:sticky lg:top-20">
          <Card className="card-soft border-primary/20 bg-gradient-to-br from-grad-green to-grad-blue">
            <CardContent className="space-y-2 p-4">
              <div className="flex items-center gap-2">
                <span className="flex size-8 items-center justify-center rounded-lg border border-primary/20 bg-card text-primary">
                  <Sparkles className="size-4" aria-hidden="true" />
                </span>
                <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Start a new conversation
                </span>
              </div>
              <p className="text-sm leading-relaxed text-brand-deep/80">
                Your mentor lives inside your courses — pick one to begin.
              </p>
            </CardContent>
          </Card>

          {catalogQ.isPending ? (
            <div className="grid grid-cols-1 gap-3">
              {Array.from({ length: 3 }, (_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : catalogQ.error ? (
            <EmptyState
              icon={GraduationCap}
              title="Couldn’t load your courses"
              description={catalogQ.error.message}
              action={
                <Button variant="outline" size="sm" onClick={() => void catalogQ.refetch()}>
                  Try again
                </Button>
              }
            />
          ) : enrolled.length === 0 ? (
            <EmptyState
              icon={GraduationCap}
              title="You’re not enrolled in any course yet"
              description="Your mentor lives inside your courses — enroll in one first."
              action={
                <Button asChild variant="outline" size="sm">
                  <Link to="/catalog">Browse courses</Link>
                </Button>
              }
            />
          ) : (
            <div className="ls-stagger grid grid-cols-1 gap-3">
              {enrolled.map((course) => (
                <NewConversationRow
                  key={course.id}
                  course={course}
                  onStart={() => setDockCourseId(course.id)}
                />
              ))}
            </div>
          )}
        </aside>
      </div>

      {dockCourseId != null ? (
        <MentorDock open courseId={dockCourseId} onClose={() => setDockCourseId(null)} />
      ) : null}
    </div>
  );
}

function SessionRow({
  session,
  onOpen,
}: {
  session: TutorSession;
  onOpen: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      data-testid="mentor-session-row"
      onClick={onOpen}
      className="card-lift press min-h-touch group w-full rounded-xl border bg-card p-4 text-left shadow-sm transition-colors hover:border-primary/40 hover:bg-primary/5"
    >
      <div className="flex items-center gap-3">
        <span
          className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-brand-blue text-primary-foreground shadow-sm"
          aria-hidden="true"
        >
          <Sparkles className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            {session.title ?? 'New conversation'}
          </p>
          <p className="truncate text-xs text-muted-foreground">{session.courseTitle}</p>
          <p className="mt-1 text-xs tabular-nums text-muted-foreground">
            {formatWhen(session.lastMessageAt)} · {session.messageCount} message
            {session.messageCount === 1 ? '' : 's'}
          </p>
        </div>
        <ChevronRight
          className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
          aria-hidden="true"
        />
      </div>
    </button>
  );
}

function NewConversationRow({
  course,
  onStart,
}: {
  course: CatalogCourse;
  onStart: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      data-testid="mentor-new-course"
      onClick={onStart}
      className="card-lift press min-h-touch group flex w-full items-center gap-3 rounded-xl border bg-card p-4 text-left shadow-sm transition-colors hover:border-primary/40 hover:bg-primary/5"
    >
      <span
        className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-card text-primary"
        aria-hidden="true"
      >
        <GraduationCap className="size-5" />
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
        {course.title}
      </span>
      <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-primary">
        <Sparkles className="size-4" aria-hidden="true" />
        Ask
      </span>
    </button>
  );
}
